import type { ICache, Cacheable, MaybePromise } from "@auth0/auth0-spa-js";

/**
 * Auth0 token cache backed by `sessionStorage` instead of `localStorage`.
 *
 * `sessionStorage` is isolated per browser tab, so each tab gets its own
 * Auth0 token cache. This lets QA/localhost testers stay logged in as
 * different accounts in different tabs of the same browser — with the
 * default `localstorage` cache, every tab shares one token cache and the
 * last login always wins in every other open tab.
 *
 * Not used in production: production still needs the shared `localstorage`
 * cache so a page refresh / new tab continues the same session.
 */
export class SessionStorageCache implements ICache {
  set<T = Cacheable>(key: string, entry: T): void {
    sessionStorage.setItem(key, JSON.stringify(entry));
  }

  get<T = Cacheable>(key: string): MaybePromise<T | undefined> {
    const value = sessionStorage.getItem(key);
    if (!value) return undefined;
    try {
      return JSON.parse(value) as T;
    } catch {
      return undefined;
    }
  }

  remove(key: string): void {
    sessionStorage.removeItem(key);
  }

  allKeys(): string[] {
    return Object.keys(sessionStorage);
  }
}
